import { FC, useCallback } from 'react';
import { css } from '@emotion/react';
import { Col, Row } from 'reactstrap';

import { useForm, useFormState } from '@/utils/hooks';
import { CompanySettings } from '@/types/company-settings';
import config, {
  pms_jobs_require_total_system_running_hours_when_reported_in_the_system,
  pms_overdue_jobs_percentage_limit,
  PMSJobsRequireTotalSystemRunningHoursWhenReportedInTheSystem,
  PMSSettingsType,
  spare_parts_inventory_survey_interval,
  spare_parts_inventory_survey_interval_unit,
  running_hours_report_interval_days,
  SparePartsInventorySurveyIntervalUnit,
  pms_assign_system_and_subsystem_or_assignment_to_group,
  pms_vessel_system_assignment_description_pattern,
  PMSAssignSystemAndSubsystemOrAssignmentToGroup,
  PMSVesselSystemAssignmentDescriptionPattern,
  pms_first_job_status_properties
} from 'views/companies/settings/pms/config';
import { FieldsStateType } from '@/common/types/form';
import { getOptionValue } from 'views/companies/settings/helpers';
import {
  pmsDefaultJobStatusProgressOptions,
  pmsVesselSystemAssignmentDescriptionPatternInformation
} from './helpers';

import Category from 'views/companies/settings/Category';
import SubCategory from 'views/companies/settings/SubCategory';
import VisibleSetting from 'views/companies/settings/VisibleSetting';
import InputLabel from 'views/companies/settings/components/InputLabel';
import NumberInput from '@/library/components/form/inputs/NumberInput';
import Select from '@/library/components/form/inputs/select';
import { CATEGORY_IDS } from '../constants';
import { useCategoryDraft } from '../useCategoryDraft';
import { formatOptionDisplay } from '../change-tracking';
import { useCompanySettingsDraft } from '../draft-context';

type PMSSettingsProps = {
  companySettings: CompanySettings | undefined;
};

const CATEGORY_ID = CATEGORY_IDS.pms;
const CATEGORY_TITLE = 'PMS';

const YES_NO_OPTIONS = [
  { label: 'No', value: '0' },
  { label: 'Yes', value: '1' }
];

const UNIT_OPTIONS = [
  { label: 'Days', value: 'days' },
  { label: 'Months', value: 'months' }
];

const ASSIGN_OPTIONS = [
  { label: 'System', value: 'system' },
  { label: 'System Assignment', value: 'system_assignment' }
];

const DESCRIPTION_PATTERN_OPTIONS = [
  {
    label: 'Vessel system description and assignment identifier',
    value: 'vessel_system_description_and_assignment_identifier'
  },
  { label: 'Vessel system assignment group', value: 'vessel_system_assignment_group' }
];

const LABELS = {
  [pms_overdue_jobs_percentage_limit]: 'PMS overdue jobs percentage limit',
  [spare_parts_inventory_survey_interval]: 'Spare parts inventory survey interval',
  [spare_parts_inventory_survey_interval_unit]: 'Spare parts inventory survey interval unit',
  [running_hours_report_interval_days]: 'Running hours report to be submitted every X days',
  [pms_jobs_require_total_system_running_hours_when_reported_in_the_system]:
    'Require total system running hours when reported in the system',
  [pms_assign_system_and_subsystem_or_assignment_to_group]:
    'Assign system and subsystem or assignment to group',
  [pms_vessel_system_assignment_description_pattern]:
    'Vessel system assignment description pattern',
  [pms_first_job_status_properties]: 'PMS first job status properties'
};

const PMSSettings: FC<PMSSettingsProps> = ({ companySettings }) => {
  const { formState, loadValues, collectValues } = useForm(config);
  const { changeField, selectField, ...rest } = useFormState(formState);
  const fields: FieldsStateType<PMSSettingsType> = rest.fields;
  const { isSettingVisible } = useCompanySettingsDraft();

  const initializeForm = useCallback(() => {
    if (!companySettings) return;

    loadValues({
      [pms_overdue_jobs_percentage_limit]: companySettings.pms_overdue_jobs_percentage_limit,
      [spare_parts_inventory_survey_interval]:
        companySettings.spare_parts_inventory_survey_interval,
      [spare_parts_inventory_survey_interval_unit]:
        companySettings.spare_parts_inventory_survey_interval_unit,
      [pms_jobs_require_total_system_running_hours_when_reported_in_the_system]:
        companySettings.pms_jobs_require_total_system_running_hours_when_reported_in_the_system,
      [pms_assign_system_and_subsystem_or_assignment_to_group]:
        companySettings.pms_assign_system_and_subsystem_or_assignment_to_group,
      [pms_vessel_system_assignment_description_pattern]:
        companySettings.pms_vessel_system_assignment_description_pattern,
      [pms_first_job_status_properties]: pmsDefaultJobStatusProgressOptions.find(
        opt => opt.value.edit_side === companySettings[pms_first_job_status_properties]?.edit_side
      ),
      [running_hours_report_interval_days]: companySettings.running_hours_report_interval_days
    });
  }, [companySettings, loadValues]);

  useCategoryDraft({
    categoryId: CATEGORY_ID,
    categoryTitle: CATEGORY_TITLE,
    companySettings,
    initializeForm,
    collectValues: (validate = true) => collectValues(validate) as PMSSettingsType | null,
    toPayload: values => [
      {
        key: pms_overdue_jobs_percentage_limit,
        value: values[pms_overdue_jobs_percentage_limit]
      },
      {
        key: spare_parts_inventory_survey_interval,
        value: values[spare_parts_inventory_survey_interval]
      },
      {
        key: spare_parts_inventory_survey_interval_unit,
        value: getOptionValue(values[spare_parts_inventory_survey_interval_unit])
      },
      {
        key: pms_assign_system_and_subsystem_or_assignment_to_group,
        value: getOptionValue(values[pms_assign_system_and_subsystem_or_assignment_to_group])
      },
      {
        key: pms_vessel_system_assignment_description_pattern,
        value: getOptionValue(values[pms_vessel_system_assignment_description_pattern])
      },
      {
        key: pms_first_job_status_properties,
        value: values[pms_first_job_status_properties]
          ? values[pms_first_job_status_properties].value
          : null
      },
      {
        key: running_hours_report_interval_days,
        value: values[running_hours_report_interval_days]
      },
      {
        key: pms_jobs_require_total_system_running_hours_when_reported_in_the_system,
        value: getOptionValue(
          values[pms_jobs_require_total_system_running_hours_when_reported_in_the_system]
        )
      }
    ],
    labels: LABELS,
    formatValue: (key, value) => {
      if (key === spare_parts_inventory_survey_interval_unit) {
        return formatOptionDisplay(value, UNIT_OPTIONS);
      }
      if (key === pms_jobs_require_total_system_running_hours_when_reported_in_the_system) {
        return formatOptionDisplay(value, YES_NO_OPTIONS);
      }
      if (key === pms_assign_system_and_subsystem_or_assignment_to_group) {
        return formatOptionDisplay(value, ASSIGN_OPTIONS);
      }
      if (key === pms_vessel_system_assignment_description_pattern) {
        return formatOptionDisplay(value, DESCRIPTION_PATTERN_OPTIONS);
      }
      if (key === pms_first_job_status_properties) {
        const match = pmsDefaultJobStatusProgressOptions.find(
          option => option.value.edit_side === (value as { edit_side?: string }).edit_side
        );
        return match?.label || formatOptionDisplay(value);
      }
      return formatOptionDisplay(value);
    },
    watch: fields
  });

  return (
    <Category id={CATEGORY_ID} title={CATEGORY_TITLE}>
      <SubCategory
        title="Jobs"
        hidden={
          !isSettingVisible(CATEGORY_ID, pms_overdue_jobs_percentage_limit) &&
          !isSettingVisible(
            CATEGORY_ID,
            pms_jobs_require_total_system_running_hours_when_reported_in_the_system
          ) &&
          !isSettingVisible(CATEGORY_ID, pms_first_job_status_properties)
        }
        className="border-bottom border-gray-200"
      >
        <Row
          className="d-flex align-items-center gx-1"
          css={css`
            .form-field {
              width: 8.5rem;
            }
          `}
        >
          <Col xs={3}>
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={pms_overdue_jobs_percentage_limit}>
              <NumberInput
                className="mb-0"
                innerClassName="h-24"
                label={
                  <InputLabel
                    title="PMS OVERDUE JOBS PERCENTAGE LIMIT"
                    apiKey={pms_overdue_jobs_percentage_limit}
                  />
                }
                placeholder="Add no."
                onChange={changeField(pms_overdue_jobs_percentage_limit)}
                {...fields[pms_overdue_jobs_percentage_limit]}
              />
            </VisibleSetting>
          </Col>
        </Row>
        <Row
          className="mt-2"
          css={css`
            .form-field {
              width: 8.5rem;
            }
          `}
        >
          <Col>
            <VisibleSetting
              categoryId={CATEGORY_ID}
              settingKey={pms_jobs_require_total_system_running_hours_when_reported_in_the_system}
            >
              <Select
                className="mb-0"
                label={
                  <InputLabel
                    title="pms jobs require total system running hours when reported in the system"
                    apiKey={pms_jobs_require_total_system_running_hours_when_reported_in_the_system}
                  />
                }
                options={YES_NO_OPTIONS}
                onChange={(option: PMSJobsRequireTotalSystemRunningHoursWhenReportedInTheSystem) =>
                  selectField(
                    pms_jobs_require_total_system_running_hours_when_reported_in_the_system
                  )(option)
                }
                {...fields[pms_jobs_require_total_system_running_hours_when_reported_in_the_system]}
              />
            </VisibleSetting>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col xs={3}>
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={pms_first_job_status_properties}>
              <Select
                className="mb-0"
                label={
                  <InputLabel
                    className="text-nowrap"
                    title="PMS FIRST JOB STATUS PROPERTIES"
                    apiKey={pms_first_job_status_properties}
                  />
                }
                options={pmsDefaultJobStatusProgressOptions}
                getOptionValue={option => option.value.edit_side}
                onChange={option => selectField(pms_first_job_status_properties)(option)}
                {...fields[pms_first_job_status_properties]}
              />
            </VisibleSetting>
          </Col>
        </Row>
      </SubCategory>

      <SubCategory
        title="Spare parts"
        hidden={
          !isSettingVisible(CATEGORY_ID, spare_parts_inventory_survey_interval) &&
          !isSettingVisible(CATEGORY_ID, spare_parts_inventory_survey_interval_unit)
        }
        className="border-bottom border-gray-200"
      >
        <Row
          className="d-flex align-items-center gx-1"
          css={css`
            .form-field {
              width: 8.5rem;
            }
          `}
        >
          <Col xs={'auto'} className="d-flex flex-column justify-content-between h-100p">
            <VisibleSetting
              categoryId={CATEGORY_ID}
              settingKey={spare_parts_inventory_survey_interval}
            >
              <InputLabel title="SPARE PARTS INVENTORY SURVEY TO BE CARRIED OUT EVERY" />
              <div className="d-flex align-items-center cmt-4">
                <NumberInput
                  className="mb-0"
                  innerClassName="h-24"
                  placeholder="Add no."
                  decimalScale={0}
                  onChange={changeField(spare_parts_inventory_survey_interval)}
                  {...fields[spare_parts_inventory_survey_interval]}
                />
                <Select
                  className="mb-0 ms-1"
                  options={UNIT_OPTIONS}
                  onChange={(option: SparePartsInventorySurveyIntervalUnit) =>
                    selectField(spare_parts_inventory_survey_interval_unit)(option)
                  }
                  {...fields[spare_parts_inventory_survey_interval_unit]}
                />
              </div>
            </VisibleSetting>
          </Col>
        </Row>
      </SubCategory>

      <SubCategory
        title="Running hours"
        hidden={!isSettingVisible(CATEGORY_ID, running_hours_report_interval_days)}
        className="border-bottom border-gray-200"
      >
        <Row
          className="d-flex align-items-center gx-1"
          css={css`
            .form-field {
              width: 8.5rem;
            }
          `}
        >
          <Col xs={'auto'} className="d-flex flex-column justify-content-between h-100p">
            <VisibleSetting
              categoryId={CATEGORY_ID}
              settingKey={running_hours_report_interval_days}
            >
              <InputLabel
                title="RUNNING HOURS REPORT TO BE SUBMITTED EVERY X DAYS"
                apiKey={running_hours_report_interval_days}
              />
              <div className="d-flex align-items-center cmt-4">
                <NumberInput
                  className="mb-0"
                  innerClassName="h-24"
                  placeholder="Add no."
                  decimalScale={0}
                  onChange={changeField(running_hours_report_interval_days)}
                  {...fields[running_hours_report_interval_days]}
                />
              </div>
            </VisibleSetting>
          </Col>
        </Row>
      </SubCategory>

      <SubCategory
        className="cpb-2 cmb-2"
        title="Systems"
        hidden={
          !isSettingVisible(CATEGORY_ID, pms_assign_system_and_subsystem_or_assignment_to_group) &&
          !isSettingVisible(CATEGORY_ID, pms_vessel_system_assignment_description_pattern)
        }
      >
        <Row
          className="mt-2"
          css={css`
            .form-field {
              width: 8.5rem;
            }
          `}
        >
          <Col>
            <VisibleSetting
              categoryId={CATEGORY_ID}
              settingKey={pms_assign_system_and_subsystem_or_assignment_to_group}
            >
              <Select
                className="mb-0"
                label={
                  <InputLabel
                    title="ASSIGN SYSTEM AND SUBSYSTEM OR ASSIGNMENT TO GROUP"
                    apiKey={pms_assign_system_and_subsystem_or_assignment_to_group}
                  />
                }
                options={ASSIGN_OPTIONS}
                onChange={(option: PMSAssignSystemAndSubsystemOrAssignmentToGroup) =>
                  selectField(pms_assign_system_and_subsystem_or_assignment_to_group)(option)
                }
                {...fields[pms_assign_system_and_subsystem_or_assignment_to_group]}
              />
            </VisibleSetting>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col xs={3}>
            <VisibleSetting
              categoryId={CATEGORY_ID}
              settingKey={pms_vessel_system_assignment_description_pattern}
            >
              <Select
                className="mb-0"
                label={
                  <InputLabel
                    className="text-nowrap"
                    title="VESSEL SYSTEM ASSIGNMENT DESCRIPTION PATTERN"
                    apiKey={pms_vessel_system_assignment_description_pattern}
                    information={pmsVesselSystemAssignmentDescriptionPatternInformation}
                    informationTooltipPlacement={'right'}
                  />
                }
                options={DESCRIPTION_PATTERN_OPTIONS}
                onChange={(option: PMSVesselSystemAssignmentDescriptionPattern) =>
                  selectField(pms_vessel_system_assignment_description_pattern)(option)
                }
                {...fields[pms_vessel_system_assignment_description_pattern]}
              />
            </VisibleSetting>
          </Col>
        </Row>
      </SubCategory>
    </Category>
  );
};

export default PMSSettings;
